import React, { useEffect, useState } from "react";

function QuizApp() {
  const authToken = localStorage.getItem("AuthToken");
  const [categories, setCategories] = useState([]);  // Initially an empty array
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [currentQuestion, setCurrentQuestion] = useState(null);
  const [options, setOptions] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [feedback, setFeedback] = useState(null);

  // Categories to fetch
  const requiredCategories = [
    { name: "Reading", api: "/Reading" },
    { name: "", api: "/Listening" },
    { name: "", api: "/Grammar&Vocab" },
    { name: "", api: "/kanji" },
  ];

  // Fetch categories from the API
  const fetchCategories = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(
        `https://api.japaking.online/api/categories`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${authToken}`,
          },
        }
      );
      if (!response.ok) {
        throw new Error("Failed to fetch categories");
      }
      const data = await response.json();
      console.log("Fetched categories:", data); // Log the response

      if (data.message && Array.isArray(data.message.categories)) {
        // Filter categories to keep only the ones we need
        const filteredCategories = data.message.categories.filter((category) =>
          requiredCategories.some((reqCat) => reqCat.name === category.category_name)
        );
        console.log("Filtered categories:", filteredCategories); // Log filtered categories
        setCategories(filteredCategories);  // Set the filtered categories
      } else {
        throw new Error("Unexpected categories data structure");
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Fetch questions for the selected category
  const fetchQuestionsAndOptions = async (categoryApi) => {
    setLoading(true);
    setError(null);
    try {
      // Fetch questions
      const questionResponse = await fetch(
        `https://api.japaking.online/api/categories${categoryApi}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${authToken}`,
          },
        }
      );

      if (!questionResponse.ok) {
        throw new Error("Failed to fetch questions");
      }

      const questionData = await questionResponse.json();

      if (questionData.message && Array.isArray(questionData.message.questions)) {
        setQuestions(questionData.message.questions);
        if (questionData.message.questions.length > 0) {
          const firstQuestion = questionData.message.questions[0];
          setCurrentQuestion(firstQuestion);
          await fetchOptions(firstQuestion.question_id);
        } else {
          setQuestions([]);
          setCurrentQuestion(null);
          setOptions([]);
        }
      } else {
        throw new Error("Unexpected question data structure");
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Fetch options for the current question
  const fetchOptions = async (questionId) => {
    try {
      const optionResponse = await fetch(
        `https://api.japaking.online/api/options?questionId=${questionId}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${authToken}`,
          },
        }
      );

      if (!optionResponse.ok) {
        throw new Error("Failed to fetch options");
      }

      const optionData = await optionResponse.json();

      if (optionData.message && Array.isArray(optionData.message.options)) {
        setOptions(optionData.message.options);
      } else {
        setOptions([]);
        throw new Error("Unexpected option data structure");
      }
    } catch (err) {
      setError(err.message);
    }
  };

  // Handle category selection
  const handleCategorySelect = async (category) => {
    setSelectedCategory(category);
    await fetchQuestionsAndOptions(category.api);
  };

  // Handle option selection
  const handleOptionSelect = (isCorrect) => {
    setFeedback(isCorrect ? "Correct!" : "Wrong!");
  };

  // Move to the next question
  const handleNextQuestion = async () => {
    const nextIndex = currentIndex + 1;
    if (nextIndex < questions.length) {
      const nextQuestion = questions[nextIndex];
      setCurrentQuestion(nextQuestion);
      setCurrentIndex(nextIndex);
      setFeedback(null); // Reset feedback when moving to next question
      await fetchOptions(nextQuestion.question_id);
    } else {
      console.log("No more questions available");
    }
  };

  useEffect(() => {
    fetchCategories(); // Fetch categories when the component mounts
  }, []);

  return (
    <div className="container mt-5">
      <h2 className="text-center">Quiz App</h2>
      {/* Category Selection */}
      <div className="row justify-content-center mt-3">
        {loading ? (
          <p>Loading categories...</p>
        ) : error ? (
          <p style={{ color: "red" }}>{error}</p>
        ) : categories.length > 0 ? (
          categories.map((category) => (
            <div
              key={category.name}
              className="col-md-2 me-3 mt-2 mb-2 text-center"
            >
              <button
                onClick={() => handleCategorySelect(category)}
                style={{
                  textDecoration: "none",
                  color: "inherit",
                  backgroundColor: "lightblue",
                  padding: "10px",
                  borderRadius: "5px",
                }}
              >
                <img
                  src={`${process.env.PUBLIC_URL}/assets/images/writingLadki.jpeg`}
                  alt={category.name}
                  className="img-fluid mb-2"
                  style={{ maxHeight: "100px" }}
                />
                <br />
                {category.name}
              </button>
            </div>
          ))
        ) : (
          <p>No categories available</p>
        )}
      </div>

      {/* Questions and Options */}
      <div className="mt-4">
        {loading ? (
          <p>Loading questions...</p>
        ) : error ? (
          <p style={{ color: "red" }}>{error}</p>
        ) : currentQuestion ? (
          <div>
            <h4>Question: {currentQuestion.question_text}</h4>
            <div className="mt-3">
              {options.map((option) => (
                <button
                  key={option.option_id}
                  onClick={() => handleOptionSelect(option.is_correct)}
                  style={{
                    margin: "5px",
                    padding: "10px",
                    backgroundColor: "lightgray",
                    border: "none",
                    borderRadius: "5px",
                  }}
                >
                  {option.option_text}
                </button>
              ))}
            </div>
            {feedback && (
              <p style={{ color: feedback === "Correct!" ? "green" : "red" }}>
                {feedback}
              </p>
            )}
            <button
              onClick={handleNextQuestion}
              disabled={currentIndex + 1 >= questions.length}
              style={{
                marginTop: "20px",
                padding: "10px 20px",
                backgroundColor: "lightblue",
                border: "none",
                borderRadius: "5px",
              }}
            >
              Next Question
            </button>
          </div>
        ) : selectedCategory ? (
          <p>No questions available for {selectedCategory.name}</p>
        ) : (
          <p>Please select a category to start the quiz</p>
        )}
      </div>
    </div>
  );
}

export default QuizApp;
